import axios from 'axios';
import { API_BASE } from './Api_base';

const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

let refreshPromise = null;

const refreshAccessToken = async () => {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
        const currentRefreshToken = localStorage.getItem('refreshToken');
        if (!currentRefreshToken) {
            throw new Error('NO_REFRESH_TOKEN');
        }

        const response = await axios.post(`${API_BASE}/auth/refresh`, {
            refreshToken: currentRefreshToken,
        });

        const newToken = response.data?.access_token;
        const newRefreshToken = response.data?.refresh_token || currentRefreshToken;
        const newExpiresAt =
            response.data?.refresh_token_expires_at ||
            Date.now() + 30 * 24 * 60 * 60 * 1000;

        localStorage.setItem('token', newToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        localStorage.setItem('refreshTokenExpiresAt', String(newExpiresAt));

        // Sync với authStore nếu đang dùng
        try {
            const { useAuthStore } = require('../stores/authStore');
            useAuthStore.getState().updateToken(newToken, newRefreshToken);
        } catch {
            // authStore chưa load, bỏ qua
        }

        return newToken;
    })().finally(() => {
        refreshPromise = null;
    });

    return refreshPromise;
};

const handleSessionExpired = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshTokenExpiresAt');
    localStorage.removeItem('expiry_banner_dismissed');

    try {
        const { useAuthStore } = require('../stores/authStore');
        useAuthStore.getState().logout();
    } catch {
        // authStore chưa load, bỏ qua
    }

    if (window.location.pathname.startsWith('/superadmin') || window.location.pathname.startsWith('/navo-admin')) {
        window.location.href = '/navo-admin/login';
    } else if (window.location.pathname.startsWith('/admin') || window.location.pathname.startsWith('/checkin')) {
        window.location.href = '/admin/login';
    }
};

// Request interceptor: Thêm token vào headers nếu có
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

// Response interceptor: 401 → refresh token → retry, nếu fail → logout
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error?.config || {};
        const status = error?.response?.status;
        const isAuthUrl =
            originalRequest?.url?.includes('/auth/login') ||
            originalRequest?.url?.includes('/auth/refresh');

        if (status === 401 && !originalRequest._retry && !isAuthUrl) {
            originalRequest._retry = true;
            try {
                const newAccessToken = await refreshAccessToken();
                if (!newAccessToken) {
                    throw new Error('REFRESH_FAILED');
                }
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return api(originalRequest);
            } catch {
                handleSessionExpired();
                return Promise.reject(error);
            }
        }

        return Promise.reject(error);
    },
);

export default api;
